import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { isValidEmail } from '../utils/validation';

const Signup = () => {
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [country, setCountry] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const navigate = useNavigate();
    const { error } = useAppSelector((state) => state.auth);
    const isOnline = useNetworkStatus();
    const { showToast } = useToast();
    const { signup, isLoading, setError } = useAuth();

    const validatePassword = (pwd: string): string | null => {
        if (pwd.length < 8) {
            return 'Password must be at least 8 characters long.';
        }
        if (!/[A-Z]/.test(pwd)) {
            return 'Password must contain at least one uppercase letter.';
        }
        if (!/[a-z]/.test(pwd)) {
            return 'Password must contain at least one lowercase letter.';
        }
        if (!/[0-9]/.test(pwd)) {
            return 'Password must contain at least one number.';
        }
        return null;
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        // Check network status first
        if (!isOnline) {
            setError('No internet connection. Please check your network and try again.');
            return;
        }

        // Validate full name
        if (!fullName.trim()) {
            setError('Please enter your full name.');
            return;
        }

        // Validate email format
        if (!isValidEmail(email)) {
            setError('Please enter a valid email address.');
            return;
        }

        // Validate country selection
        if (!country) {
            setError('Please select your country.');
            return;
        }

        // Validate password strength
        const passwordError = validatePassword(password);
        if (passwordError) {
            setError(passwordError);
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match. Please try again.');
            return;
        }

        // Call API
        const result = await signup({ firstName: fullName,lastName: '', email, password });

        if (result.success) {
            showToast('Account created successfully! Please verify your email.', 'success');
            navigate('/email-verification');
        } else {
            showToast(result.error || 'Signup failed', 'error');
        }
    };

    // Clear error when user starts typing
    const handleInputChange = (setter: (value: string) => void) => (value: string) => {
        if (error) setError(null);
        setter(value);
    };

    return (
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background-secondary)', padding: 'var(--spacing-2xl) var(--spacing-md)' }}>
            <div className="max-w-md w-full" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xl)' }}>
                <div>
                    <h2 className="text-center font-extrabold" style={{ marginTop: 'var(--spacing-lg)', fontSize: 'var(--font-size-3xl)', color: 'var(--color-text-primary)' }}>
                        Create your account
                    </h2>
                </div>
                <form style={{ marginTop: 'var(--spacing-xl)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-lg)' }} onSubmit={handleSubmit}>
                    {error && (
                        <div className="flex items-center" style={{ backgroundColor: 'var(--color-danger-100)', border: '1px solid var(--color-danger-400)', color: 'var(--color-danger-700)', padding: 'var(--spacing-md)', borderRadius: 'var(--radius-md)', gap: 'var(--spacing-sm)' }}>
                            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{error}</span>
                        </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                        <div>
                            <label htmlFor="fullName" className="block font-medium" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-xs)' }}>
                                Full Name
                            </label>
                            <input
                                id="fullName"
                                name="fullName"
                                type="text"
                                autoComplete="name"
                                required
                                className="appearance-none block w-full"
                                style={{ padding: 'var(--spacing-md)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', color: 'var(--color-text-primary)', backgroundColor: 'var(--color-surface)', fontSize: 'var(--font-size-sm)' }}
                                placeholder="Enter your full name"
                                value={fullName}
                                onChange={(e) => handleInputChange(setFullName)(e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="email" className="block font-medium" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-xs)' }}>
                                Email address
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                className="appearance-none block w-full"
                                style={{ padding: 'var(--spacing-md)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', color: 'var(--color-text-primary)', backgroundColor: 'var(--color-surface)', fontSize: 'var(--font-size-sm)' }}
                                placeholder="Enter your email"
                                value={email}
                                onChange={(e) => handleInputChange(setEmail)(e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="country" className="block font-medium" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-xs)' }}>
                                Country
                            </label>
                            <select
                                id="country"
                                name="country"
                                autoComplete="country"
                                required
                                className="appearance-none block w-full"
                                style={{ padding: 'var(--spacing-md)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', color: 'var(--color-text-primary)', backgroundColor: 'var(--color-surface)', fontSize: 'var(--font-size-sm)' }}
                                value={country}
                                onChange={(e) => handleInputChange(setCountry)(e.target.value)}
                            >
                                <option value="" disabled>Select your country</option>
                                <option value="united-states">United States</option>
                                <option value="india">India</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="password" className="block font-medium" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-xs)' }}>
                                Password
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="new-password"
                                required
                                className="appearance-none block w-full"
                                style={{ padding: 'var(--spacing-md)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', color: 'var(--color-text-primary)', backgroundColor: 'var(--color-surface)', fontSize: 'var(--font-size-sm)' }}
                                placeholder="Create a password"
                                value={password}
                                onChange={(e) => handleInputChange(setPassword)(e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="confirmPassword" className="block font-medium" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-xs)' }}>
                                Confirm Password
                            </label>
                            <input
                                id="confirmPassword"
                                name="confirmPassword"
                                type="password"
                                autoComplete="new-password"
                                required
                                className="appearance-none block w-full"
                                style={{ padding: 'var(--spacing-md)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', color: 'var(--color-text-primary)', backgroundColor: 'var(--color-surface)', fontSize: 'var(--font-size-sm)' }}
                                placeholder="Confirm your password"
                                value={confirmPassword}
                                onChange={(e) => handleInputChange(setConfirmPassword)(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Password requirements hint */}
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-gray-50)', padding: 'var(--spacing-md)', borderRadius: 'var(--radius-lg)' }}>
                        <p className="font-medium" style={{ marginBottom: 'var(--spacing-xs)' }}>Password requirements:</p>
                        <ul className="list-disc list-inside" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                            <li>At least 8 characters long</li>
                            <li>Contains uppercase and lowercase letters</li>
                            <li>Contains at least one number</li>
                        </ul>
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="group relative w-full flex justify-center font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ padding: 'var(--spacing-md)', border: 'none', fontSize: 'var(--font-size-sm)', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-primary-600)' }}
                        >
                            {isLoading ? 'Creating account...' : 'Sign up'}
                        </button>
                    </div>

                    <div className="text-center">
                        <Link
                            to="/login"
                            className="font-medium"
                            style={{ color: 'var(--color-primary-600)' }}
                        >
                            Already have an account? Sign in
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Signup;
